import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { createContext, useContext, useState } from 'react';

interface PortalDropdownContextValue {
  close: () => void;
}

const PortalDropdownContext = createContext<PortalDropdownContextValue | null>(
  null
);

export const usePortalDropdown = () => {
  const context = useContext(PortalDropdownContext);
  if (!context) {
    throw new Error(
      'usePortalDropdown must be used within PortalDropdown component'
    );
  }
  return context;
};

export interface PortalDropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  placement?:
    | 'bottom'
    | 'top'
    | 'left'
    | 'right'
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'left-start'
    | 'left-end'
    | 'right-start'
    | 'right-end';
  dismissOnClick?: boolean;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

export const PortalDropdown: React.FC<PortalDropdownProps> = ({
  trigger,
  children,
  placement = 'bottom',
  dismissOnClick = false,
  className = '',
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    placement,
    middleware: [offset(5), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, {
      enabled: isOpen,
      outsidePress: true,
      escapeKey: true,
    }),
  ]);

  const handleContentClick = () => {
    if (dismissOnClick) {
      setIsOpen(false);
    }
  };

  const close = () => {
    handleOpenChange(false);
  };

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="relative"
      >
        {trigger}
      </div>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 99999,
            }}
            className={`rounded-lg bg-white shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.05)] ${className}`}
            {...getFloatingProps()}
            onClick={handleContentClick}
          >
            <PortalDropdownContext.Provider value={{ close }}>
              {children}
            </PortalDropdownContext.Provider>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
